<template>
    <kiosk-layout>
        <page-loading v-if="loading"></page-loading>
        <div v-else>
            <page-header class="mb-6 lg:mb-12">
                <template v-slot:subtitle>
                    <a href="/kiosk/teams" class="no-underline! text-sky-600! text-base font-normal">
                        <font-awesome-icon widthAuto class="mr-1" :icon="faArrowLeft"></font-awesome-icon>
                        Search
                    </a>
                </template>

                {{ team.name }}

                <template v-if="team.status !== 'closed'" v-slot:actions>
                    <vx-button
                        v-if="team.status === 'active'"
                        @click="handleSuspend"
                        data-test="suspend"
                        color="muted-dark"
                    >
                        Suspend
                        <font-awesome-icon widthAuto :icon="faPause"></font-awesome-icon>
                    </vx-button>
                    <vx-button v-else @click="handleActivate" data-test="activate" color="muted-dark">
                        {{ team.status === 'suspended' ? 'Re-Activate' : 'Activate' }}
                        <font-awesome-icon widthAuto :icon="faPlay"></font-awesome-icon>
                    </vx-button>

                    <vx-button
                        color="primary"
                        :href="`/kiosk/users/${team.owner_id}/impersonate?team_id=${team.id}`"
                        :disabled="team.owner_id === currentUserId || team.status === 'closed'"
                        data-test="impersonate-owner"
                    >
                        Impersonate Owner <font-awesome-icon widthAuto :icon="faMasksTheater"></font-awesome-icon>
                    </vx-button>
                </template>
            </page-header>

            <div class="grid grid-cols-12">
                <page-sidebar :links="navigationLinks"></page-sidebar>

                <slot></slot>
            </div>
        </div>

        <kiosk-team-billing-review-modal
            v-if="team"
            v-model:visible="billingReviewModal"
            v-model:team="team"
            :action="billingReviewAction"
        ></kiosk-team-billing-review-modal>
    </kiosk-layout>
</template>

<script setup>
import { faArrowLeft, faMasksTheater, faPause, faPlay } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { onMounted, ref, computed } from 'vue';
import PageLoading from '~/components/general/PageLoading.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageSidebar from '~/components/general/PageSidebar.vue';
import { VxButton } from '@voxie/frontend-components';
import { useTeamDetails } from '../composables/useTeamDetails';
import KioskLayout from '~/components/kiosk/KioskLayout.vue';
import KioskTeamBillingReviewModal from '../modals/KioskTeamBillingReviewModal.vue';
import { match } from 'path-to-regexp';

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
});

const { mount, team, loading } = useTeamDetails();

const billingReviewModal = ref(false);
const billingReviewAction = ref('activate');

const currentUserId = Spark.state.user.id;

const handleActivate = () => {
    billingReviewAction.value = 'activate';
    billingReviewModal.value = true;
};

const handleSuspend = () => {
    billingReviewAction.value = 'suspend';
    billingReviewModal.value = true;
};

const navigationLinks = computed(() => {
    const links = [
        { href: `/kiosk/teams/${props.teamId}`, label: 'Details' },
        { href: `/kiosk/teams/${props.teamId}/configuration`, label: 'Configuration' },
        {
            href: `/kiosk/teams/${props.teamId}/phone-numbers`,
            label: 'Phone Numbers',
            pathMatcher: match('/kiosk/teams/:teamId/phone-numbers{/*path}'),
        },
    ];

    links.push({ href: `/kiosk/teams/${props.teamId}/billing-config`, label: 'Billing Config' });

    const isBillable = !team.value?.parent_id || team.value?.is_billable;
    if (isBillable) {
        links.push({ href: `/kiosk/teams/${props.teamId}/billing-details`, label: 'Billing Details' });

        if (team.value?.status !== 'closed') {
            links.push({ href: `/kiosk/teams/${props.teamId}/proforma-invoices`, label: 'Upcoming Invoices' });
        }

        links.push({ href: `/kiosk/teams/${props.teamId}/historical-invoices`, label: 'Historical Invoices' });
    }

    links.push({ href: `/kiosk/teams/${props.teamId}/features`, label: 'Features' });

    if (team.value?.team_attributes?.single_sign_on || team.value?.require_sso) {
        links.push({ href: `/kiosk/teams/${props.teamId}/sso`, label: 'Single Sign-On' });
    }

    links.push(
        {
            href: `/kiosk/teams/${props.teamId}/users`,
            label: 'Users',
            pathMatcher: match(['/kiosk/teams/:teamId/users{/*path}', '/kiosk/teams/:teamId/invites{/*path}']),
        },
        { href: `/kiosk/teams/${props.teamId}/users/imports`, label: 'User Imports' },
        { href: `/kiosk/teams/${props.teamId}/conversions/imports`, label: 'Conversion Imports' }
    );

    if (team.value?.team_attributes?.contact_groups) {
        links.push({ href: `/kiosk/teams/${props.teamId}/terms`, label: 'Terms & Conditions' });
        links.push({ href: `/kiosk/teams/${props.teamId}/groups`, label: 'Groups' });
        links.push({ href: `/kiosk/teams/${props.teamId}/groups/imports`, label: 'Group Imports' });
    }

    if (!team.value?.parent_id) {
        links.push(
            { href: `/kiosk/teams/${props.teamId}/sub-teams`, label: 'Sub Teams' },
            { href: `/kiosk/teams/${props.teamId}/sub-teams/features`, label: 'Sub Teams Features' }
        );
    }

    links.push({ href: `/kiosk/teams/${props.teamId}/emergency-tools`, label: 'Emergency Tools' });

    return links;
});

onMounted(() => {
    mount(props.teamId);
});
</script>
